import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { tryCreate, tryDelete, tryGet, tryList, tryUpdate } from "./_shared";

export type Ticket = Tables<"tickets">;
export type TicketInsert = Omit<TablesInsert<"tickets">, "company_id">;
export type TicketUpdate = TablesUpdate<"tickets">;

export const ticketService = {
  get(id: string) {
    return tryGet<Ticket>(`/api/tickets/${encodeURIComponent(id)}`);
  },
  getWithRelations(id: string) {
    return tryGet<Ticket & { status_ref?: { name: string } | null }>(
      `/api/tickets/${encodeURIComponent(id)}?withRelations=true`,
    );
  },
  list(_companyId: string) {
    return tryList<Ticket>("/api/tickets");
  },
  listByInvoice(invoiceId: string) {
    return tryList<Ticket>(`/api/tickets?invoiceId=${encodeURIComponent(invoiceId)}`);
  },
  getByInvoice(invoiceId: string) {
    return tryGet<Ticket>(`/api/tickets/by-invoice/${encodeURIComponent(invoiceId)}`);
  },
  create(data: TicketInsert, _context: ServiceContext) {
    return tryCreate<Ticket>("/api/tickets", data);
  },
  update(id: string, data: TicketUpdate, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryUpdate<Ticket>(`/api/tickets/${encodeURIComponent(id)}`, data);
  },
  // Server-side cascade handles: attachments cleanup, source PDF removal,
  // ticket row delete (cascades to ticket_items via FK), and history.
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/tickets/${encodeURIComponent(id)}`);
  },
  updateStatus(id: string, newStatusId: string, _previousStatusId: string | null, _context: ServiceContext) {
    return tryUpdate<Ticket>(`/api/tickets/${encodeURIComponent(id)}`, { status_id: newStatusId });
  },
  linkToInvoice(id: string, invoiceId: string | null, _previousInvoiceId: string | null, _context: ServiceContext) {
    return tryUpdate<Ticket>(`/api/tickets/${encodeURIComponent(id)}`, { invoice_id: invoiceId });
  },
};
